import { tagKompakt } from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { useZuweisen } from '../lib/mutationen'
import { vorschlagQuery } from '../lib/queries'
import { AbwesenheitsHinweis } from './Abwesenheit'
import { Button } from './ui/primitives'
import { hintergrundSchliesst } from './ui/hintergrund'

/**
 * Aufgabe zuweisen, mit Systemvorschlag (Kap. 4.3, AP 4.6).
 *
 * **Zuweisen ist die Ausnahme.** Deshalb steht es hier in einem Dialog und nicht
 * als Knopf an jeder Zeile: Ziehen soll der bequemere Weg bleiben. Wer die
 * Oberfläche so baut, dass Zuweisen leichter ist, kehrt das Konzept um.
 *
 * Der Vorschlag sortiert nach Anwesenheit, dann passenden Fähigkeiten, dann
 * freier Kapazität. Solange keine Fähigkeiten gepflegt sind, sind es Anwesenheit
 * und Kapazität – und das sagt der Dialog, statt Kompetenz-Treffer vorzutäuschen.
 *
 * ## Abwesenheit warnt, sie sperrt nicht (Z-01, entschieden von Nils)
 *
 * Wer heute weg ist, steht unten und trägt „abwesend bis …" – **zuweisen lässt er
 * sich trotzdem.** Etwas für nächste Woche vorzubereiten ist ein legitimer
 * Vorgang; eine harte Sperre hätte ihn verboten und einen Umgehungsweg gebraucht.
 *
 * Der Knopf des ersten Eintrags heißt weiter „Zuweisen", trägt aber **nicht** die
 * hervorgehobene Form, wenn diese Person abwesend ist: sind alle weg, gibt es
 * keinen Vorschlag, den man empfehlen könnte. Ein hervorgehobener Knopf an einer
 * abwesenden Person wäre eine Empfehlung, die der Sortierung widerspricht.
 */
export function Zuweisen({
  aufgabeId,
  titel,
  schliessen,
}: {
  aufgabeId: string
  titel: string
  schliessen: () => void
}) {
  const { data: vorschlag } = useQuery(vorschlagQuery(aufgabeId))

  /**
   * Die Liste dahinter ändert sich sofort (AP 4.7); der **Dialog** wartet aber auf
   * die Antwort, bevor er schließt.
   *
   * Bewusst so: Zuweisen ist die Ausnahme und wird selten geklickt – ein
   * abgewiesener Versuch („Person ist deaktiviert") muss dort erklärt werden, wo
   * geklickt wurde. Schließt der Dialog vorher, hat die Meldung keinen Ort mehr.
   * Leitplanke 1 gilt für Standardaktionen; das ist Ziehen, und das wartet nicht.
   */
  const zuweisen = useZuweisen()

  const personen = vorschlag?.personen ?? []
  const ohneSkills = (vorschlag?.gesuchteSkills.length ?? 0) === 0
  const alleAbwesend = personen.length > 0 && personen.every((p) => p.abwesend)

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-50 flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[4vh] sm:p-6 sm:pt-[10vh]"
      {...hintergrundSchliesst(schliessen)}
      role="dialog"
      aria-modal="true"
      aria-label="Aufgabe zuweisen"
    >
      <div className="flex max-h-[min(90vh,100%)] sm:max-h-[min(76vh,100%)] w-full min-w-0 max-w-[520px] flex-col overflow-y-auto rounded-card border border-line bg-surface p-[18px] shadow-card">
        <h3 className="text-sm font-bold">Zuweisen</h3>
        <p className="mt-1 truncate text-[12.5px] text-ink2" title={titel}>
          {titel}
        </p>

        <p className="mt-2 text-[11px] text-muted">
          {ohneSkills
            ? 'Sortiert nach Anwesenheit, dann freier Kapazität. Fähigkeiten sind noch nicht gepflegt – sobald sie es sind, gehen sie vor der Kapazität.'
            : 'Sortiert nach Anwesenheit, dann passenden Fähigkeiten, dann freier Kapazität.'}{' '}
          Ziehen bleibt der Normalfall; Zuweisen ist für das, was liegen bleibt.
        </p>

        {alleAbwesend && (
          /**
           * Der Fall, der sonst unbemerkt bliebe: alle sind weg, und die Liste
           * sieht aus wie immer. Dann ist die Reihenfolge keine Empfehlung mehr,
           * und das gehört gesagt statt durch einen hervorgehobenen Knopf
           * überdeckt.
           */
          <p
            role="status"
            className="mt-2.5 rounded-lg bg-[#fdf3dc] px-2.5 py-2 text-[12px] text-[#8a6100]"
          >
            Heute ist niemand aus dieser Liste da. Zuweisen geht trotzdem – die Aufgabe liegt dann
            bereit, bis die Person zurück ist.
          </p>
        )}

        {zuweisen.error && (
          <p
            role="alert"
            className="mt-2.5 rounded-lg bg-[#fdeaea] px-2.5 py-2 text-[12.5px] text-[#b02a2a]"
          >
            {zuweisen.error.message}
          </p>
        )}

        <div className="mt-3">
          {personen.length === 0 && (
            <p className="text-[13px] text-ink2">
              Niemand mit Anmeldekonto verfügbar. Zuweisen an jemanden ohne Konto wäre sinnlos – er
              könnte die Aufgabe nicht sehen. Konten legt die Administration an: KI &amp;
              Einstellungen → Benutzer.
            </p>
          )}
          {personen.length === 1 && (
            <p className="mb-1.5 text-[11px] text-muted">
              Nur eine Person hat ein Anmeldekonto. Wer keines hat, erscheint hier nicht – sonst
              würde die Aufgabe jemandem zugewiesen, der sie nie zu sehen bekommt.
            </p>
          )}
          {personen.map((p, i) => (
            <div
              key={p.personId}
              className="flex items-center gap-2 border-b border-line py-2 text-[12.5px] last:border-b-0"
            >
              <span className={`font-semibold ${p.abwesend ? 'text-ink2' : ''}`}>
                {p.displayName}
              </span>
              {i === 0 && personen.length > 1 && !p.abwesend && (
                <span className="rounded-md bg-accent-soft px-[7px] py-[2.5px] text-[10px] font-semibold text-accent-deep">
                  Vorschlag
                </span>
              )}
              <AbwesenheitsHinweis
                angabe={{
                  abwesendBis: p.abwesendBis,
                  halberTag: p.halberTag,
                  naechsteVon: p.naechsteVon,
                  naechsteBis: p.naechsteBis,
                }}
              />
              {p.kommtInFrage && (
                <span
                  className="rounded-md bg-[#f1effb] px-[7px] py-[2.5px] text-[10px] font-semibold text-[#4a3aa7]"
                  title="Steht an der Aufgabe unter „kommen in Frage“"
                >
                  kommt in Frage
                </span>
              )}
              {p.treffer > 0 && (
                <span className="text-[11px] text-muted">
                  {p.treffer} passende {p.treffer === 1 ? 'Fähigkeit' : 'Fähigkeiten'}
                </span>
              )}
              <span
                className={`tabular ml-auto text-[11px] ${
                  p.frei <= 0 ? 'font-semibold text-pool-sofort' : 'text-ink2'
                }`}
              >
                {p.inArbeit} / {p.wipLimit}
                {p.frei <= 0 && ' · voll'}
              </span>
              <Button
                variant={i === 0 && !p.abwesend ? 'primary' : 'ghost'}
                size="sm"
                disabled={zuweisen.isPending}
                onClick={() =>
                  zuweisen.mutate(
                    { id: aufgabeId, personId: p.personId, personName: p.displayName },
                    { onSuccess: schliessen },
                  )
                }
                // Sonst heißen bis zu zwölf Knöpfe gleich „Zuweisen" – und die
                // Abwesenheit steht nur daneben, nicht im vorgelesenen Text.
                aria-label={
                  p.abwesendBis
                    ? `${p.displayName} zuweisen – abwesend bis ${tagKompakt(p.abwesendBis)}`
                    : `${p.displayName} zuweisen`
                }
              >
                Zuweisen
              </Button>
            </div>
          ))}
        </div>

        <p className="mt-2.5 text-[11px] text-muted">
          Die Aufgabe verlässt damit alle Pools – sie hat jemanden, also darf sie niemand mehr
          ziehen. Der Status bleibt: zugewiesen heißt „das ist deine", nicht „du arbeitest dran".
          Über dem WIP-Limit zuzuweisen ist erlaubt; ein Limit, das die Steuerung blockiert, wäre
          kein Steuerungswerkzeug.
        </p>

        <div className="mt-3 flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={zuweisen.isPending}
            onClick={() =>
              zuweisen.mutate(
                { id: aufgabeId, personId: null, personName: null },
                { onSuccess: schliessen },
              )
            }
          >
            Zuweisung aufheben
          </Button>
          <Button variant="ghost" size="sm" onClick={schliessen}>
            Abbrechen
          </Button>
        </div>
      </div>
    </div>
  )
}
